import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import { ErrorAlert } from '@bibliocommons/alerts';
import CharacterCount from '@bibliocommons/base-character-count';
import { getValidHtmlAttributes } from '@bibliocommons/utils-html';

import FormattedHTMLMessage from 'app/components/shared/FormattedHTMLMessage';

import './TextArea.scss';

export default function TextArea(props) {
  const {
    id,
    value,
    label,
    handleChange,
    handleBlur,
    errorMessage,
    maxLength,
    suggestedMaxLength,
    fitToText,
    minHeight,
    optional,
    displayMessage,
    className,
    dataKey,
    autoFocus,
    labelledById,
    describedById,
    testId
  } = props;

  const [height, setHeight] = useState('0');
  const textareaRef = useRef(null);

  const updateHeightToFitText = useCallback(() => {
    setHeight('inherit');
  }, []);

  useEffect(() => {
    if (height === 'inherit' && textareaRef.current) {
      setHeight(`${textareaRef.current.scrollHeight}`);
    }
  }, [height]);

  useEffect(() => {
    if (fitToText) {
      updateHeightToFitText();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function getAriaDescribedBy() {
    const describedByIds = [];

    if (describedById) {
      describedByIds.push(describedById);
    }

    if (errorMessage) {
      describedByIds.push(`${id}-error`);
    }

    if (maxLength || suggestedMaxLength) {
      describedByIds.push(`${id}-character-count`);
    }

    return describedByIds.length > 0 ? describedByIds.join(' ') : undefined;
  }

  function getTextAreaStyle() {
    const style = {};
    if (minHeight) {
      style.minHeight = minHeight;
    }
    if (height && fitToText) {
      style.height = `${height}px`;
      style.overflow = 'hidden';
    }
    return style;
  }

  function handleTextAreaChange() {
    const { value: textareaValue } = textareaRef.current;

    if (fitToText) {
      updateHeightToFitText();
    }

    if (handleChange) {
      handleChange(textareaValue);
    }
  }

  function handleTextAreaBlur() {
    if (handleBlur) {
      handleBlur();
    }
  }

  function renderCharacterCount() {
    if (suggestedMaxLength || maxLength) {
      return (
        <div id={`${id}-character-count`}>
          <CharacterCount count={value ? value.length : 0} limit={suggestedMaxLength || maxLength} />
        </div>
      );
    }

    return null;
  }

  return (
    <div className={cn('cp-text-area', className, { 'text-area--invalid': errorMessage })} data-key={dataKey}>
      {label && (
        <label htmlFor={id}>
          {label}
          {optional && (
            <span className="text-area__label__optional">
              (<FormattedMessage id="optional" />)
            </span>
          )}
        </label>
      )}
      {displayMessage && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div className="text-area__label__message" tabIndex="0">
          <FormattedHTMLMessage id={displayMessage} />
        </div>
      )}
      <textarea
        {...getValidHtmlAttributes(props, 'textarea')}
        aria-describedby={getAriaDescribedBy()}
        aria-labelledby={labelledById}
        autoFocus={autoFocus}
        className="text-area__textarea"
        id={id}
        ref={textareaRef}
        onChange={handleTextAreaChange}
        onBlur={handleTextAreaBlur}
        data-key="text-area"
        data-test-id={testId}
        style={getTextAreaStyle()}
      />
      {errorMessage && (
        <div id={`${id}-error`}>
          <ErrorAlert>{errorMessage}</ErrorAlert>
        </div>
      )}
      {renderCharacterCount()}
    </div>
  );
}

TextArea.propTypes = {
  fitToText: PropTypes.bool,
  maxLength: PropTypes.number.isRequired,
  suggestedMaxLength: PropTypes.number,
  id: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
  errorMessage: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
  value: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  optional: PropTypes.bool,
  dataKey: PropTypes.string,
  className: PropTypes.string,
  minHeight: PropTypes.string,
  displayMessage: PropTypes.string,
  autoFocus: PropTypes.bool,
  labelledById: PropTypes.string,
  describedById: PropTypes.string,
  testId: PropTypes.string
};

TextArea.defaultProps = {
  fitToText: false
};
